import styled from 'styled-components';
import type {TicketSla} from '../types/ticket';

type SlaVariant = 'paused' | 'breached' | 'warning' | 'ontrack';

// The three states that read as a pill (paused/breached/at-risk) carry a filled
// background and a hairline; on-track is text-only so it recedes. The semantic
// colour drives both, and the background is the same surface token for each, so a
// variant needs only its own colour looked up.
const PILL_VARIANTS: SlaVariant[] = ['paused', 'breached', 'warning'];

// Just the slice of the semantic token tree this badge reads, so the helper states
// its own requirements rather than depending on the design system's full theme type
// (which is unavailable when the package resolves to a stub).
interface SlaColorTokens {
	color: {
		text: {content: {auxiliary: string}; semantic: {error: string; success: string}};
		border: {warning: string};
	};
}

const variantColor = (variant: SlaVariant, sys: SlaColorTokens): string => {
	switch (variant) {
		case 'paused':
			return sys.color.text.content.auxiliary;
		case 'breached':
			return sys.color.text.semantic.error;
		case 'warning':
			return sys.color.border.warning;
		case 'ontrack':
			return sys.color.text.semantic.success;
	}
};

const SlaBadgeBase = styled.span<{$variant: SlaVariant}>`
	display: inline-flex;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[100]};
	${p => p.theme.sys.font?.body?.['2']}
	color: ${p => variantColor(p.$variant, p.theme.sys)};
	${p =>
		PILL_VARIANTS.includes(p.$variant) &&
		`
		/* 9999px is a sanctioned exception: the design system has no full-round
		   radius token (see docs/spec/ticketing/frontend/sky-theme.md). */
		border-radius: 9999px;
		background: ${p.theme.sys.color.background.disabled['1']};
		border: ${p.theme.sys.size.borderwidth.thin} solid ${variantColor(p.$variant, p.theme.sys)};
		padding: ${p.theme.sys.spacing[0]} ${p.theme.sys.spacing[200]};
		font-weight: 500;
	`}
`;

const SlaIcon = styled.svg`
	height: 12px;
	width: 12px;
`;

export function formatElapsedTime(seconds: number): string {
	const hours = Math.floor(seconds / 3600);
	if (hours >= 24) {
		const days = Math.floor(hours / 24);
		const remainingHours = hours % 24;
		return `${days}d ${remainingHours}h`;
	}
	const minutes = Math.floor((seconds % 3600) / 60);
	return `${hours}h ${minutes}m`;
}

export function SLABadge({ticket}: {ticket: {sla: TicketSla | null | undefined}}) {
	const {sla} = ticket;

	if (!sla) return null;

	if (sla.paused) {
		return (
			<SlaBadgeBase $variant="paused">
				<SlaIcon fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
					<circle cx="12" cy="12" r="10" />
					<path d="M10 9l2 2 4-4" />
				</SlaIcon>
				paused
			</SlaBadgeBase>
		);
	}

	if (sla.breached) {
		return (
			<SlaBadgeBase $variant="breached">
				<SlaIcon fill="currentColor" viewBox="0 0 20 20">
					<circle cx="10" cy="10" r="10" />
				</SlaIcon>
				breached
			</SlaBadgeBase>
		);
	}

	const ratio = sla.thresholdSeconds > 0 ? sla.elapsedSeconds / sla.thresholdSeconds : 0;

	if (ratio >= 0.8) {
		return (
			<SlaBadgeBase $variant="warning">
				<SlaIcon fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
					<circle cx="12" cy="12" r="10" />
					<path d="M12 6v6l4 2" />
				</SlaIcon>
				{formatElapsedTime(sla.elapsedSeconds)}
			</SlaBadgeBase>
		);
	}

	return (
		<SlaBadgeBase $variant="ontrack">
			<SlaIcon fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
				<circle cx="12" cy="12" r="10" />
				<path d="M12 6v6l4 2" />
			</SlaIcon>
			{formatElapsedTime(sla.elapsedSeconds)}
		</SlaBadgeBase>
	);
}
